/**
 * DocumentJournalView — יומן מסמכים
 *
 * Phase 27: Document Journal for the Inventory module.
 * Phase 43: Delivery Note → Tax Invoice / Tax Invoice+Receipt conversion.
 *
 * Displays all inventory_documents for the active company with:
 *  • Date-range, client, and document-type filters
 *  • Sortable table with totals summary row
 *  • Click-to-view modal using InventoryDocumentPrintView
 *  • Print (useReactToPrint) and Email (mailto: placeholder) actions in modal
 *  • [NEW] Convert תעודת משלוח → חשבונית מס / חשבונית מס קבלה via RPC
 *  • [NEW] "already invoiced" badge on delivery notes that have a linked invoice
 *  • [NEW] based_on_document_id traceability (B2B Order → DN → Invoice)
 *
 * Data sources (read-only, standard Supabase JS client):
 *  - inventory_documents       → main rows
 *  - document_types            → name, prefix  (joined via document_type_id)
 *  - inventory_clients         → name           (joined via client_id)
 *  - inventory_document_lines  → line items     (fetched on demand for the modal)
 *  - inventory_products        → product names  (fetched on demand for the modal)
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import {
  Loader2,
  AlertCircle,
  Eye,
  Printer,
  Mail,
  X,
  ClipboardList,
  Filter,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  FileText,
  Receipt,
  Link2,
  ChevronRight,
  Copy,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { usePermissions } from '@/hooks/usePermissions';
import { ExternalInvoiceActions, type ExternalProvider, type ExternalIssued } from '@/components/inventory/ExternalInvoiceActions';
import { readTranzilaSettings, type TranzilaSettings } from '@/lib/tranzilaSettings';
import SendDocumentEmailModal from '@/components/modals/SendDocumentEmailModal';
import {
  InventoryDocumentPrintView,
  type InventoryPrintData,
  type InventoryPrintLine,
} from '@/components/inventory/InventoryDocumentPrintView';

// ─── Constants ─────────────────────────────────────────────────────────────────

const DELIVERY_NOTE_NAME  = 'תעודת משלוח';
const CLIENT_RETURN_NAME  = 'תעודת החזרה מלקוח';
const TAX_INVOICE_NAME    = 'חשבונית מס';
const TAX_RECEIPT_NAME    = 'חשבונית מס קבלה';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface DocRow {
  id:                    string;
  document_number:       number;
  issue_date:            string;
  created_at:            string;
  subtotal:              number | null;
  vat_amount:            number | null;
  total:                 number | null;
  notes:                 string | null;
  remarks:               string | null;
  // joined
  doc_type_name:         string;
  doc_type_prefix:       string;
  doc_type_id:           string;
  client_name:           string | null;
  client_email:          string | null;
  client_id:             string | null;
  // Phase 43 traceability
  based_on_document_id:  string | null;
  b2b_order_id:          string | null;
  issued_by_name:        string | null;
  rivhit_invoice_number:   string | null;
  rivhit_invoice_url:      string | null;
  tranzila_invoice_number: string | null;
  tranzila_invoice_url:    string | null;
}

interface LinkedInvoice {
  id:                    string;
  document_number:       number;
  doc_type_name:         string;
  doc_type_prefix:       string;
  issue_date:            string;
  total:                 number | null;
  rivhit_invoice_number: string | null;
  rivhit_invoice_url:    string | null;
}

interface DocTypeOption {
  id:     string;
  name:   string;
  prefix: string;
}

interface ClientOption {
  id:   string;
  name: string;
}

interface RawLine {
  id:          string;
  product_id:  string | null;
  quantity:    number;
  unit_price:  number;
  discount_pct: number;
  line_total:  number;
  inventory_products: { name: string } | null;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString('he-IL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return iso; }
};

const fmtCurrency = (n: number | null | undefined): string => {
  if (n == null) return '—';
  return `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const safeNum = (n: number | null | undefined): number => n ?? 0;

const docRef = (prefix: string, num: number) =>
  `${prefix}${String(num).padStart(4, '0')}`;

// ─── Convert-to-Invoice confirmation dialog ────────────────────────────────────

function ConvertInvoiceDialog({
  doc,
  existingInvoices,
  converting,
  onConvert,
  onClose,
}: {
  doc:              DocRow;
  existingInvoices: LinkedInvoice[];
  converting:       boolean;
  onConvert:        (targetTypeName: string) => void;
  onClose:          () => void;
}) {
  const hasInvoice    = existingInvoices.some(i => i.doc_type_name === TAX_INVOICE_NAME);
  const hasReceipt    = existingInvoices.some(i => i.doc_type_name === TAX_RECEIPT_NAME);

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div
        dir="rtl"
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2.5">
            <FileText className="w-5 h-5 text-blue-600" />
            <h2 className="text-base font-bold text-gray-900">הפקת חשבונית ממסמך</h2>
          </div>
          <button
            onClick={onClose}
            disabled={converting}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Source doc info */}
          <div className="flex items-center gap-2.5 px-3.5 py-3 bg-blue-50 border border-blue-100 rounded-xl">
            <Link2 className="w-4 h-4 text-blue-500 shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-blue-800">{DELIVERY_NOTE_NAME}</span>
              <span className="text-blue-600 font-mono mx-1.5">
                {docRef(doc.doc_type_prefix, doc.document_number)}
              </span>
              <span className="text-blue-500">· {fmtDate(doc.issue_date)}</span>
              {doc.client_name && (
                <span className="text-blue-500 block text-xs mt-0.5">{doc.client_name}</span>
              )}
            </div>
          </div>

          {/* Already-invoiced warning */}
          {existingInvoices.length > 0 && (
            <div className="px-3.5 py-3 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
              <div className="flex items-center gap-2 text-amber-700 text-xs font-semibold">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                מסמך זה כבר שויך לחשבוניות הבאות:
              </div>
              {existingInvoices.map(inv => (
                <div key={inv.id} className="flex items-center gap-2 text-xs text-amber-700 pr-5">
                  <ChevronRight className="w-3 h-3 shrink-0" />
                  <span className="font-medium">{inv.doc_type_name}</span>
                  <span className="font-mono">{docRef(inv.doc_type_prefix, inv.document_number)}</span>
                  {inv.rivhit_invoice_number && (
                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold">
                      רווחית #{inv.rivhit_invoice_number}
                    </span>
                  )}
                </div>
              ))}
              <p className="text-xs text-amber-600 pr-5">ניתן להפיק חשבונית נוספת, אך שים לב לכפילות.</p>
            </div>
          )}

          {/* Totals preview */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="px-3 py-2 bg-gray-50 rounded-xl">
              <div className="text-[10px] text-gray-400 font-medium mb-0.5">לפני מע״מ</div>
              <div className="text-sm font-bold text-gray-800">{fmtCurrency(doc.subtotal)}</div>
            </div>
            <div className="px-3 py-2 bg-gray-50 rounded-xl">
              <div className="text-[10px] text-gray-400 font-medium mb-0.5">מע״מ</div>
              <div className="text-sm font-bold text-gray-800">{fmtCurrency(doc.vat_amount)}</div>
            </div>
            <div className="px-3 py-2 bg-blue-50 rounded-xl">
              <div className="text-[10px] text-blue-500 font-medium mb-0.5">סה״כ לתשלום</div>
              <div className="text-sm font-bold text-blue-800">{fmtCurrency(doc.total)}</div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-2 pt-1">
            {/* חשבונית מס */}
            <button
              onClick={() => onConvert(TAX_INVOICE_NAME)}
              disabled={converting}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl
                         bg-blue-600 hover:bg-blue-700 text-white font-semibold
                         text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {converting
                ? <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                : <FileText className="w-4 h-4 shrink-0" />
              }
              <div className="flex-1 text-right">
                <div>הפק {TAX_INVOICE_NAME}</div>
                {hasInvoice && (
                  <div className="text-xs text-blue-200 font-normal">(חשבונית נוספת)</div>
                )}
              </div>
            </button>

            {/* חשבונית מס קבלה */}
            <button
              onClick={() => onConvert(TAX_RECEIPT_NAME)}
              disabled={converting}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl
                         bg-emerald-600 hover:bg-emerald-700 text-white font-semibold
                         text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {converting
                ? <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                : <Receipt className="w-4 h-4 shrink-0" />
              }
              <div className="flex-1 text-right">
                <div>הפק {TAX_RECEIPT_NAME}</div>
                {hasReceipt && (
                  <div className="text-xs text-emerald-200 font-normal">(חשבונית נוספת)</div>
                )}
              </div>
            </button>

            {/* Cancel */}
            <button
              onClick={onClose}
              disabled={converting}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200
                         text-gray-600 hover:bg-gray-50 text-sm font-medium
                         transition-colors disabled:opacity-50"
            >
              ביטול
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Print Modal ──────────────────────────────────────────────────────────────

function PrintModal({
  doc,
  companyName,
  companyId,
  externalProviders,
  tranzilaSettings,
  onExternalIssued,
  onClose,
  onConvertClick,
  linkedInvoices,
  onCreateFromDocument,
}: {
  doc:            DocRow;
  companyName:    string;
  companyId:      string;
  externalProviders: ExternalProvider[];
  tranzilaSettings:  TranzilaSettings | null;
  onExternalIssued:  (docId: string, provider: ExternalProvider, result: ExternalIssued) => void;
  onClose:        () => void;
  onConvertClick: () => void;
  linkedInvoices: LinkedInvoice[];
  onCreateFromDocument?: (docId: string) => void;
}) {
  const [printData,  setPrintData]  = useState<InventoryPrintData | null>(null);
  const [linesLoading, setLinesLoading] = useState(true);
  const [linesError,   setLinesError]   = useState<string | null>(null);

  const printRef  = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: printRef });

  // Fetch document lines + product names on mount
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLinesLoading(true);
      setLinesError(null);
      try {
        const { data, error } = await supabase
          .from('inventory_document_lines')
          .select(`
            id,
            product_id,
            quantity,
            unit_price,
            discount_pct,
            line_total,
            inventory_products ( name )
          `)
          .eq('document_id', doc.id)
          .order('line_no');

        if (error) throw error;
        if (cancelled) return;

        const rawLines = (data ?? []) as unknown as RawLine[];

        const lines: InventoryPrintLine[] = rawLines.map(l => ({
          product_id:   l.product_id,
          product_name: l.inventory_products?.name ?? `מוצר ${l.product_id ?? ''}`,
          quantity:     l.quantity,
          unit_price:   l.unit_price,
          discount_pct: l.discount_pct,
          line_total:   l.line_total,
        }));

        const pd: InventoryPrintData = {
          companyName,
          docTypeName: doc.doc_type_name,
          docNumber:   docRef(doc.doc_type_prefix, doc.document_number),
          date:        doc.issue_date,
          clientName:  doc.client_name ?? undefined,
          lines,
          subtotal:    safeNum(doc.subtotal),
          vatAmount:   safeNum(doc.vat_amount),
          grandTotal:  safeNum(doc.total),
          remarks:     doc.remarks ?? undefined,
          issuedByName: doc.issued_by_name ?? undefined,
        };

        setPrintData(pd);
      } catch (e) {
        if (!cancelled) {
          setLinesError(e instanceof Error ? e.message : 'שגיאה בטעינת שורות');
        }
      } finally {
        if (!cancelled) setLinesLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [doc, companyName]);

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const emailDocLabel = `${doc.doc_type_name} ${docRef(doc.doc_type_prefix, doc.document_number)}`;

  const isDeliveryNote = doc.doc_type_name === DELIVERY_NOTE_NAME;
  const isClientReturn = doc.doc_type_name === CLIENT_RETURN_NAME;
  const creditEnabled  = isClientReturn && !!tranzilaSettings?.credit_enabled;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto pt-20 pb-8 px-4">
      <div className="w-full max-w-[240mm] space-y-4">

        {/* Toolbar */}
        <div className="flex items-center justify-between bg-white rounded-2xl shadow-lg px-5 py-3 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="px-3 py-1 rounded-full text-xs font-semibold border border-blue-200 bg-blue-50 text-blue-700">
              {doc.doc_type_name}
            </span>
            <span className="text-base font-bold text-gray-800">
              {docRef(doc.doc_type_prefix, doc.document_number)}
            </span>
            <span className="text-sm text-gray-400">{fmtDate(doc.issue_date)}</span>
            {doc.client_name && (
              <span className="text-sm text-gray-500">· {doc.client_name}</span>
            )}

            {/* Phase 43: show linked invoices badge */}
            {linkedInvoices.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {linkedInvoices.map(inv => (
                  <span
                    key={inv.id}
                    className="flex items-center gap-1 px-2 py-0.5 bg-green-100
                               text-green-700 text-[11px] font-semibold rounded-full border border-green-200"
                    title={`חשבונית מקושרת: ${inv.doc_type_name} ${docRef(inv.doc_type_prefix, inv.document_number)}`}
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    {docRef(inv.doc_type_prefix, inv.document_number)}
                    {inv.rivhit_invoice_number && ` · רווחית #${inv.rivhit_invoice_number}`}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">

            {/* Phase 43: Convert to invoice — only on delivery notes */}
            {isDeliveryNote && (
              <button
                onClick={onConvertClick}
                disabled={linesLoading}
                title="הפק חשבונית מס מתעודת משלוח זו"
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700
                           text-white text-sm font-medium rounded-lg transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="w-4 h-4" />
                הפק חשבונית
              </button>
            )}

            <ExternalInvoiceActions
              docId={doc.id}
              docLabel={`${doc.doc_type_name} ${docRef(doc.doc_type_prefix, doc.document_number)}`}
              clientName={doc.client_name}
              clientEmail={doc.client_email}
              total={safeNum(doc.total)}
              lineCount={printData ? printData.lines.length : null}
              enabled={isDeliveryNote ? externalProviders : creditEnabled ? ['tranzila'] : []}
              credit={isClientReturn}
              tranzilaSettings={tranzilaSettings}
              issued={{
                ...(doc.tranzila_invoice_number ? { tranzila: { number: doc.tranzila_invoice_number, url: doc.tranzila_invoice_url } } : {}),
                ...(doc.rivhit_invoice_number ? { rivhit: { number: doc.rivhit_invoice_number, url: doc.rivhit_invoice_url } } : {}),
              }}
              disabled={linesLoading}
              onIssued={(provider, result) => onExternalIssued(doc.id, provider, result)}
            />

            <button
              onClick={() => handlePrint()}
              disabled={!printData || linesLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700
                         text-white text-sm font-medium rounded-lg transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Printer className="w-4 h-4" />
              הדפס / שמור כ-PDF
            </button>
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200
                         text-gray-700 text-sm font-medium rounded-lg transition-colors"
            >
              <Mail className="w-4 h-4" />
              שלח במייל
            </button>
            {onCreateFromDocument && (
              <button
                onClick={() => onCreateFromDocument(doc.id)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-50 hover:bg-orange-100
                           text-orange-700 text-sm font-medium rounded-lg transition-colors
                           border border-orange-200"
              >
                <Copy className="w-4 h-4" />
                צור מסמך על בסיס
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content area */}
        {linesLoading && (
          <div className="flex items-center justify-center py-24 gap-3 text-gray-400 bg-white rounded-2xl shadow">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm">טוען שורות מסמך…</span>
          </div>
        )}

        {linesError && (
          <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {linesError}
          </div>
        )}

        {/* Hidden print target */}
        {printData && (
          <div style={{ display: 'none' }}>
            <InventoryDocumentPrintView ref={printRef} data={printData} />
          </div>
        )}

        {/* Document preview (visible) */}
        {printData && !linesLoading && (
          <InventoryDocumentPrintView data={printData} />
        )}
      </div>

      {showEmailModal && (
        <SendDocumentEmailModal
          onClose={() => setShowEmailModal(false)}
          clientId={doc.client_id}
          companyId={companyId}
          documentId={doc.id}
          docLabel={emailDocLabel}
        />
      )}
    </div>
  );
}

// ─── Multi-select dropdown for doc types ──────────────────────────────────────

function DocTypeMultiSelect({
  options,
  selected,
  onChange,
}: {
  options:  DocTypeOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allSelected  = selected.size === 0;
  const label = allSelected
    ? 'כל סוגי המסמכים'
    : `${selected.size} סוגים נבחרו`;

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };

  const clearAll = () => onChange(new Set());

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg
                   text-sm bg-white hover:border-gray-400 transition-colors min-w-[180px]
                   justify-between"
      >
        <span className="truncate text-gray-700">{label}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
              : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
      </button>

      {open && (
        <div className="absolute top-full mt-1 right-0 w-60 bg-white border border-gray-200
                        rounded-xl shadow-lg z-20 py-1 max-h-64 overflow-y-auto">
          {/* Clear all */}
          <button
            onClick={clearAll}
            className="w-full text-right px-4 py-2 text-xs text-blue-600 hover:bg-blue-50 transition-colors font-medium"
          >
            {allSelected ? '✓ כל הסוגים' : 'נקה בחירה'}
          </button>
          <hr className="border-gray-100 mx-2" />
          {options.map(o => (
            <label
              key={o.id}
              className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={selected.has(o.id)}
                onChange={() => toggle(o.id)}
                className="w-4 h-4 accent-blue-600"
              />
              <span className="text-gray-700">{o.name}</span>
              <span className="text-xs text-gray-400 font-mono ml-auto">{o.prefix}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  companyId:   string;
  companyName: string;
  onCreateFromDocument?: (docId: string) => void;
}

export function DocumentJournalView({ companyId, companyName, onCreateFromDocument }: Props) {

  // ── Data state ─────────────────────────────────────────────────────────────
  const [docs,       setDocs]       = useState<DocRow[]>([]);
  const [docTypes,   setDocTypes]   = useState<DocTypeOption[]>([]);
  const [clients,    setClients]    = useState<ClientOption[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  // ── Phase 43: linked-invoices cache keyed by delivery-note id ──────────────
  // We load on demand when a delivery note is opened.
  const [linkedInvoicesMap, setLinkedInvoicesMap] = useState<Record<string, LinkedInvoice[]>>({});

  // ── External invoicing (Tranzila / Rivhit): connection record must exist ──
  const { can } = usePermissions();
  const canIssueExternal = can({ module: 'inventory', level: 'editor' });
  const [externalProviders, setExternalProviders] = useState<ExternalProvider[]>([]);
  const [tranzilaSettings,  setTranzilaSettings]  = useState<TranzilaSettings | null>(null);

  useEffect(() => {
    setTranzilaSettings(null);
    if (!companyId || !canIssueExternal) { setExternalProviders([]); return; }
    supabase
      .from('company_integrations')
      .select('provider_name, settings')
      .eq('company_id', companyId)
      .in('provider_name', ['tranzila', 'rivhit'])
      .then(({ data, error: e }) => {
        if (e || !data) { setExternalProviders([]); return; }
        const tz = data.find(r => r.provider_name === 'tranzila');
        setTranzilaSettings(tz ? readTranzilaSettings(tz.settings) : null);
        setExternalProviders(
          data
            .filter(r => (r.settings as Record<string, unknown> | null)?.manual_push_documents !== false)
            .map(r => r.provider_name as ExternalProvider),
        );
      });
  }, [companyId, canIssueExternal]);

  const handleExternalIssued = (docId: string, provider: ExternalProvider, result: ExternalIssued) => {
    const patch = provider === 'tranzila'
      ? { tranzila_invoice_number: result.number, tranzila_invoice_url: result.url }
      : { rivhit_invoice_number: result.number, rivhit_invoice_url: result.url };
    setDocs(prev => prev.map(d => (d.id === docId ? { ...d, ...patch } : d)));
    setViewDoc(prev => (prev && prev.id === docId ? { ...prev, ...patch } : prev));
  };

  // ── Filter state ───────────────────────────────────────────────────────────
  const [dateFrom,     setDateFrom]     = useState<string>('');
  const [dateTo,       setDateTo]       = useState<string>('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [typeFilter,   setTypeFilter]   = useState<Set<string>>(new Set());

  // ── Modal / conversion state ───────────────────────────────────────────────
  const [viewDoc,       setViewDoc]       = useState<DocRow | null>(null);
  const [convertDoc,    setConvertDoc]    = useState<DocRow | null>(null);
  const [converting,    setConverting]    = useState(false);
  const [convertToast,  setConvertToast]  = useState<{ ok: boolean; msg: string } | null>(null);

  // ── Fetch all documents (with join data) ───────────────────────────────────
  const fetchDocs = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: rawDocs, error: docsErr } = await supabase
        .from('inventory_documents')
        .select(`
          id,
          document_number,
          issue_date,
          created_at,
          subtotal,
          vat_amount,
          total,
          notes,
          remarks,
          client_id,
          document_type_id,
          issued_by_name,
          rivhit_invoice_number,
          rivhit_invoice_url,
          tranzila_invoice_number,
          tranzila_invoice_url,
          document_types ( id, name, prefix ),
          inventory_clients ( id, name, email )
        `)
        .eq('company_id', companyId)
        .order('issue_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (docsErr) throw docsErr;

      const mapped: DocRow[] = (rawDocs ?? []).map((r: any) => ({
        id:                   r.id,
        document_number:      r.document_number,
        issue_date:           r.issue_date,
        created_at:           r.created_at,
        subtotal:             r.subtotal,
        vat_amount:           r.vat_amount,
        total:                r.total,
        notes:                r.notes,
        remarks:              r.remarks,
        doc_type_id:          r.document_types?.id  ?? r.document_type_id ?? '',
        doc_type_name:        r.document_types?.name ?? '—',
        doc_type_prefix:      r.document_types?.prefix ?? '',
        client_id:            r.inventory_clients?.id ?? r.client_id ?? null,
        client_name:          r.inventory_clients?.name ?? null,
        client_email:         r.inventory_clients?.email ?? null,
        issued_by_name:       r.issued_by_name ?? null,
        rivhit_invoice_number:   r.rivhit_invoice_number ?? null,
        rivhit_invoice_url:      r.rivhit_invoice_url ?? null,
        tranzila_invoice_number: r.tranzila_invoice_number ?? null,
        tranzila_invoice_url:    r.tranzila_invoice_url ?? null,
        // Phase 43 columns — populated below once migration has run
        based_on_document_id: null,
        b2b_order_id:         null,
      }));

      // Phase 43: try to fetch linkage columns (based_on_document_id, b2b_order_id).
      // These columns only exist after running Phase 43 SQL migration, so we
      // wrap in try/catch and silently ignore if the columns aren't there yet.
      // The conversion button (הפק חשבונית) still appears because it only
      // depends on doc_type_name, NOT on these linkage columns.
      try {
        const ids = mapped.map(d => d.id);
        if (ids.length > 0) {
          const { data: linkData } = await supabase
            .from('inventory_documents')
            .select('id, based_on_document_id, b2b_order_id')
            .in('id', ids);
          if (linkData) {
            const linkMap: Record<string, { based_on: string|null; b2b: string|null }> = {};
            for (const l of linkData as any[]) {
              linkMap[l.id] = { based_on: l.based_on_document_id ?? null, b2b: l.b2b_order_id ?? null };
            }
            for (const d of mapped) {
              const lk = linkMap[d.id];
              if (lk) { d.based_on_document_id = lk.based_on; d.b2b_order_id = lk.b2b; }
            }
          }
        }
      } catch {
        // columns don't exist yet — gracefully ignored, traceability badges hidden
      }

      setDocs(mapped);

      // Derive filter options from loaded data
      const typeMap   = new Map<string, DocTypeOption>();
      const clientMap = new Map<string, ClientOption>();
      for (const d of mapped) {
        if (d.doc_type_id) {
          typeMap.set(d.doc_type_id, { id: d.doc_type_id, name: d.doc_type_name, prefix: d.doc_type_prefix });
        }
        if (d.client_id && d.client_name) {
          clientMap.set(d.client_id, { id: d.client_id, name: d.client_name });
        }
      }
      setDocTypes([...typeMap.values()].sort((a, b) => a.name.localeCompare(b.name, 'he')));
      setClients([...clientMap.values()].sort((a, b) => a.name.localeCompare(b.name, 'he')));

      // Pre-compute linked-invoices map:
      // An invoice's based_on_document_id points to its source delivery note.
      // Build a map: sourceDocId → [invoice rows]
      const invMap: Record<string, LinkedInvoice[]> = {};
      for (const d of mapped) {
        if (d.based_on_document_id) {
          const srcId = d.based_on_document_id;
          if (!invMap[srcId]) invMap[srcId] = [];
          invMap[srcId]!.push({
            id:                    d.id,
            document_number:       d.document_number,
            doc_type_name:         d.doc_type_name,
            doc_type_prefix:       d.doc_type_prefix,
            issue_date:            d.issue_date,
            total:                 d.total,
            rivhit_invoice_number: d.rivhit_invoice_number,
            rivhit_invoice_url:    d.rivhit_invoice_url,
          });
        }
      }
      setLinkedInvoicesMap(invMap);

    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת מסמכים');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // ── Phase 43: open convert dialog (pre-load linked invoices for this doc) ──
  const openConvertDialog = useCallback((doc: DocRow) => {
    setConvertDoc(doc);
    setConvertToast(null);
  }, []);

  // ── Phase 43: perform the conversion RPC ───────────────────────────────────
  const handleConvert = useCallback(async (targetTypeName: string) => {
    if (!convertDoc) return;
    setConverting(true);
    setConvertToast(null);
    try {
      const { data: newDocId, error: rpcErr } = await supabase.rpc(
        'convert_document_to_invoice',
        {
          p_source_doc_id:        convertDoc.id,
          p_target_doc_type_name: targetTypeName,
        },
      );
      if (rpcErr) throw rpcErr;

      const newRef = newDocId ? String(newDocId).slice(-8).toUpperCase() : '—';
      setConvertToast({
        ok:  true,
        msg: `${targetTypeName} הופקה בהצלחה (מזהה: …${newRef})`,
      });

      // Refresh the document list so the new invoice appears
      await fetchDocs();

      // Auto-close the dialog after a short delay
      setTimeout(() => {
        setConvertDoc(null);
        setConvertToast(null);
      }, 2500);

    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setConvertToast({ ok: false, msg });
    } finally {
      setConverting(false);
    }
  }, [convertDoc, fetchDocs]);

  // ── Filtered docs ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return docs.filter(d => {
      if (dateFrom && d.issue_date < dateFrom) return false;
      if (dateTo   && d.issue_date > dateTo)   return false;
      if (clientFilter && d.client_id !== clientFilter) return false;
      if (typeFilter.size > 0 && !typeFilter.has(d.doc_type_id)) return false;
      return true;
    });
  }, [docs, dateFrom, dateTo, clientFilter, typeFilter]);

  // ── Summary totals (filtered only) ────────────────────────────────────────
  const summarySubtotal  = filtered.reduce((s, d) => s + safeNum(d.subtotal),   0);
  const summaryVat       = filtered.reduce((s, d) => s + safeNum(d.vat_amount), 0);
  const summaryTotal     = filtered.reduce((s, d) => s + safeNum(d.total),      0);

  const hasFilters = dateFrom || dateTo || clientFilter || typeFilter.size > 0;

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setClientFilter('');
    setTypeFilter(new Set());
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div dir="rtl" className="space-y-4">

      {/* ── Filters bar ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">

          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
            <Filter className="w-3.5 h-3.5" />
            סינון
          </div>

          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500 whitespace-nowrap">מתאריך</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500 whitespace-nowrap">עד תאריך</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500 whitespace-nowrap">לקוח</label>
            <select
              value={clientFilter}
              onChange={e => setClientFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[150px]"
            >
              <option value="">כל הלקוחות</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500 whitespace-nowrap">סוג מסמך</label>
            <DocTypeMultiSelect
              options={docTypes}
              selected={typeFilter}
              onChange={setTypeFilter}
            />
          </div>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600
                         hover:bg-red-50 rounded-lg transition-colors border border-red-200"
            >
              <X className="w-3.5 h-3.5" />
              נקה סינון
            </button>
          )}

          <span className="mr-auto text-xs text-gray-400">
            {filtered.length} מתוך {docs.length} מסמכים
          </span>
        </div>
      </div>

      {/* ── Loading / Error ──────────────────────────────────────────────── */}
      {loading && (
        <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-sm">טוען יומן מסמכים…</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* ── Phase 43: global conversion toast ───────────────────────────── */}
      {convertToast && !convertDoc && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm border shadow-sm
                         ${convertToast.ok
                           ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                           : 'bg-red-50 border-red-200 text-red-700'}`}>
          {convertToast.ok
            ? <CheckCircle2 className="w-4 h-4 shrink-0" />
            : <AlertCircle  className="w-4 h-4 shrink-0" />}
          {convertToast.msg}
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────────────── */}
      {!loading && !error && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-gray-400">
              <ClipboardList className="w-12 h-12 text-gray-200" />
              <p className="text-sm font-medium">
                {docs.length === 0 ? 'אין מסמכים עדיין' : 'אין מסמכים התואמים את הסינון'}
              </p>
              {hasFilters && docs.length > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-blue-600 hover:underline"
                >
                  נקה סינון להצגת כל המסמכים
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Column headers */}
              <div className="grid grid-cols-[120px_130px_1fr_1fr_110px_90px_110px_auto_48px]
                              gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100
                              text-xs font-semibold text-gray-400 uppercase tracking-wide">
                <span>תאריך</span>
                <span>מספר מסמך</span>
                <span>סוג מסמך</span>
                <span>לקוח</span>
                <span className="text-left">לפני מע״מ</span>
                <span className="text-left">מע״מ</span>
                <span className="text-left">סה״כ</span>
                <span>קישור</span>
                <span />
              </div>

              {/* Rows */}
              <div className="divide-y divide-gray-50">
                {filtered.map(doc => {
                  const isDN          = doc.doc_type_name === DELIVERY_NOTE_NAME;
                  const linkedInvs    = linkedInvoicesMap[doc.id] ?? [];
                  const isInvoiced    = isDN && linkedInvs.length > 0;
                  const isLinkedInv   = !!doc.based_on_document_id;

                  return (
                    <div
                      key={doc.id}
                      className={`grid grid-cols-[120px_130px_1fr_1fr_110px_90px_110px_auto_48px]
                                  gap-3 px-5 py-3.5 items-center
                                  hover:bg-blue-50/40 transition-colors cursor-pointer group
                                  ${isLinkedInv ? 'bg-violet-50/20' : ''}`}
                      onClick={() => setViewDoc(doc)}
                    >
                      <span className="text-sm text-gray-600">{fmtDate(doc.issue_date)}</span>

                      <span className="text-sm font-mono font-bold text-blue-700">
                        {docRef(doc.doc_type_prefix, doc.document_number)}
                      </span>

                      <span className="text-sm text-gray-700 truncate">{doc.doc_type_name}</span>

                      <span className="text-sm text-gray-600 truncate">{doc.client_name ?? '—'}</span>

                      <span className="text-sm text-gray-700 text-left font-medium">
                        {fmtCurrency(doc.subtotal)}
                      </span>

                      <span className="text-sm text-gray-500 text-left">
                        {fmtCurrency(doc.vat_amount)}
                      </span>

                      <span className="text-sm font-bold text-gray-900 text-left">
                        {fmtCurrency(doc.total)}
                      </span>

                      {/* Phase 43: traceability badges */}
                      <div className="flex flex-col gap-1 min-w-0">
                        {isInvoiced && linkedInvs.map(inv => (
                          <span
                            key={inv.id}
                            className="flex items-center gap-1 text-[10px] font-semibold
                                       px-1.5 py-0.5 bg-green-100 text-green-700
                                       border border-green-200 rounded-full whitespace-nowrap"
                            title={`חשבונית: ${inv.doc_type_name}`}
                          >
                            <CheckCircle2 className="w-2.5 h-2.5 shrink-0" />
                            {docRef(inv.doc_type_prefix, inv.document_number)}
                          </span>
                        ))}
                        {isLinkedInv && (
                          <span
                            className="flex items-center gap-1 text-[10px] font-semibold
                                       px-1.5 py-0.5 bg-violet-100 text-violet-700
                                       border border-violet-200 rounded-full whitespace-nowrap"
                            title="חשבונית זו הופקה ממסמך אחר"
                          >
                            <Link2 className="w-2.5 h-2.5 shrink-0" />
                            מקור
                          </span>
                        )}
                      </div>

                      <button
                        onClick={e => { e.stopPropagation(); setViewDoc(doc); }}
                        className="flex items-center justify-center p-2 text-gray-400
                                   group-hover:text-blue-600 group-hover:bg-blue-100
                                   rounded-lg transition-colors"
                        title="צפה במסמך"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Summary footer */}
              <div className="grid grid-cols-[120px_130px_1fr_1fr_110px_90px_110px_auto_48px]
                              gap-3 px-5 py-3.5 bg-gray-50 border-t-2 border-gray-200">
                <span className="col-span-4 text-xs font-bold text-gray-500 uppercase tracking-wide self-center">
                  סיכום ({filtered.length} מסמכים)
                </span>
                <span className="text-sm font-bold text-gray-800 text-left">
                  {fmtCurrency(summarySubtotal)}
                </span>
                <span className="text-sm font-bold text-gray-600 text-left">
                  {fmtCurrency(summaryVat)}
                </span>
                <span className="text-sm font-bold text-blue-700 text-left text-base">
                  {fmtCurrency(summaryTotal)}
                </span>
                <span />
                <span />
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Print Modal ──────────────────────────────────────────────────── */}
      {viewDoc && (
        <PrintModal
          doc={viewDoc}
          companyName={companyName}
          companyId={companyId}
          externalProviders={externalProviders}
          tranzilaSettings={tranzilaSettings}
          onExternalIssued={handleExternalIssued}
          linkedInvoices={linkedInvoicesMap[viewDoc.id] ?? []}
          onClose={() => setViewDoc(null)}
          onConvertClick={() => {
            setViewDoc(null);
            openConvertDialog(viewDoc);
          }}
          onCreateFromDocument={onCreateFromDocument ? (docId) => {
            setViewDoc(null);
            onCreateFromDocument(docId);
          } : undefined}
        />
      )}

      {/* ── Phase 43: Convert-to-Invoice dialog ─────────────────────────── */}
      {convertDoc && (
        <>
          <ConvertInvoiceDialog
            doc={convertDoc}
            existingInvoices={linkedInvoicesMap[convertDoc.id] ?? []}
            converting={converting}
            onConvert={handleConvert}
            onClose={() => { setConvertDoc(null); setConvertToast(null); }}
          />
          {/* Toast inside dialog area */}
          {convertToast && (
            <div
              className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[400]
                           flex items-center gap-3 px-5 py-3.5 rounded-2xl
                           text-sm font-medium shadow-xl border
                           ${convertToast.ok
                             ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                             : 'bg-red-50 border-red-200 text-red-700'}`}
            >
              {convertToast.ok
                ? <CheckCircle2 className="w-4 h-4 shrink-0" />
                : <AlertCircle  className="w-4 h-4 shrink-0" />}
              {convertToast.msg}
            </div>
          )}
        </>
      )}
    </div>
  );
}
